import React, { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import { Actions as ActionsIcon, Delete as DeleteIcon } from "@/components/icons";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type { TableColumn } from "@/types/component";
import { getAdminUsers } from "@/lib/users.api";
import { personnelGroupsApi } from "../services/personnel-groups.api";
import type { PersonnelGroupDetail } from "../types/personnel-groups.types";
import AddMemberModal from "../components/AddMemberModal";
import styles from "./PersonnelGroupDetailPage.module.scss";

const PersonnelGroupDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canView = hasPermission(PERMISSIONS.PERSONNEL_GROUPS_VIEW);
  const canUpdate = hasPermission(PERMISSIONS.PERSONNEL_GROUPS_UPDATE);
  const { groupId } = useParams<{ groupId: string }>();
  const [detail, setDetail] = useState<PersonnelGroupDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [addOpen, setAddOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<{ id: string; name: string }[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const fetchDetail = useCallback(async () => {
    if (!groupId || !canView) {
      setDetail(null);
      return;
    }
    setLoading(true);
    try {
      const data = await personnelGroupsApi.getById(groupId, { page: currentPage, limit: pageSize });
      setDetail(data);
    } finally {
      setLoading(false);
    }
  }, [canView, groupId, currentPage, pageSize]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const loadUsers = useCallback(async () => {
    const { data } = await getAdminUsers({ page: 1, limit: 100 });
    setUserOptions(data.map((u) => ({ id: u.id, name: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || u.id })));
  }, []);

  const columns: TableColumn<(NonNullable<PersonnelGroupDetail["members"]>[number])>[] = [
    {
      header: t("personnelGroups.memberName", "Ad Soyad"),
      accessor: (r) => [r.user.firstName, r.user.lastName].filter(Boolean).join(" ") || "-",
      className: styles.nameColumn,
    },
    {
      header: t("personnelGroups.unit", "Birim"),
      accessor: (r) => r.user.department?.name || "-",
      className: styles.departmentColumn,
    },
  ];

  const renderActions = (row: NonNullable<PersonnelGroupDetail["members"]>[number]) => {
    if (!canUpdate) return null;
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;
    const menuContent = (
      <div className={styles.actionMenu}>
        <button
          className={styles.actionMenuItem}
          onClick={async (e) => {
            e.stopPropagation();
            if (!groupId) return;
            try {
              await personnelGroupsApi.removeMember(groupId, row.userID);
            } catch (error) {
              const status = (error as AxiosError)?.response?.status;
              if (status !== 404) {
                toast.error(t("personnelGroups.operationFailed", "İşlem başarısız"));
              }
            } finally {
              handleDropdownToggle(rowId, false);
              fetchDetail();
            }
          }}
        >
          <DeleteIcon width={20} height={20} />
          {t("common.remove")}
        </button>
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("personnelGroups.title")} />
      <Breadcrumb items={[{ label: t("personnelGroups.title"), onClick: () => navigate("/dashboard/personnel-groups") }, { label: detail?.name ?? t("common.detail", "Detay") }]} />
      <PageHeader onAddNew={canUpdate ? async () => { await loadUsers(); setAddOpen(true); } : undefined} addButtonLabel={canUpdate ? t("personnelGroups.addMember", "Personel Ekle") : undefined} />
      <Table
        columns={columns}
        data={detail?.members ?? []}
        isLoading={loading}
        getRowId={(r) => r.id}
        totalRecords={detail?.totalCount ?? 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
      />

      <AddMemberModal
        isOpen={addOpen}
        users={userOptions}
        onClose={() => setAddOpen(false)}
        onSubmit={async (userIds) => {
          if (!groupId) return;
          try {
            await personnelGroupsApi.addMembers(groupId, userIds);
            setAddOpen(false);
            fetchDetail();
          } catch (error) {
            const status = (error as AxiosError)?.response?.status;
            if (status === 400) {
              toast.error(t("personnelGroups.invalidMemberSelection", "Seçilen personellerden bazıları geçersiz. Lütfen listeyi yenileyip tekrar deneyin."));
              return;
            }
            toast.error(t("personnelGroups.operationFailed", "İşlem başarısız"));
          }
        }}
      />
    </DashboardLayout>
  );
};

export default PersonnelGroupDetailPage;
